<template>
  <b-card>
    <b-card-title class="mb-3">
      Expiry Label
    </b-card-title>

    <b-alert
      variant="danger"
      :show="loadingError ? true : false"
      dismissible
      @dismissed="loadingError = null"
    >
      <div class="alert-body">
        {{ loadingError }}
      </div>
    </b-alert>

    <b-row>
      <b-col
        md="5"
        sm="12"
      >
        <b-form-group label="Select First Supplier">
          <custom-v-select
            v-model="selectedSupplier"
            :options="supplierOptions"
            label="identifier"
            placeholder="Select First Supplier"
          />
        </b-form-group>
      </b-col>
      <b-col
        md="5"
        sm="12"
      >
        <b-form-group label="Select Second Supplier (Optional)">
          <custom-v-select
            v-model="selectedSupplier2"
            :options="supplierOptions"
            label="identifier"
            placeholder="Select Second Supplier"
          />
        </b-form-group>
      </b-col>
      <b-col
        md="2"
        sm="12"
        class="d-flex align-items-end"
      >
        <b-form-group class="w-100">
          <b-button
            variant="primary"
            block
            :disabled="!selectedSupplier || printing"
            @click="handlePrint"
          >
            <b-spinner
              v-if="printing"
              small
              class="mr-1"
            />
            <feather-icon
              v-else
              icon="PrinterIcon"
              class="mr-1"
            />
            Print
          </b-button>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- First Supplier Preview -->
    <b-row class="mt-3">
      <b-col md="6">
        <div class="address-box">
          <h5 class="address-label">
            TO:
          </h5>
          <div v-if="selectedSupplier">
            <p class="address-content">
              <strong>{{ selectedSupplier.identifier }}</strong>
            </p>
            <p
              v-if="selectedSupplier.address"
              class="address-content"
            >
              {{ formatAddressForDisplay(selectedSupplier.address) }}
            </p>
            <p
              v-else
              class="text-muted mb-0"
            >
              Address not available
            </p>
          </div>
          <p
            v-else
            class="text-muted mb-0"
          >
            Please select a supplier
          </p>
        </div>
      </b-col>
      <b-col md="6">
        <div class="address-box">
          <h5 class="address-label">
            FROM:
          </h5>
          <p
            v-if="vatikaAddress"
            class="address-content"
          >
            {{ formatAddressForDisplay(vatikaAddress) }}
          </p>
          <p
            v-else
            class="text-muted"
          >
            Loading...
          </p>
        </div>
      </b-col>
    </b-row>

    <!-- Second Supplier Preview -->
    <b-row
      v-if="selectedSupplier2"
      class="mt-3"
    >
      <b-col md="6">
        <div class="address-box">
          <h5 class="address-label">
            TO:
          </h5>
          <div>
            <p class="address-content">
              <strong>{{ selectedSupplier2.identifier }}</strong>
            </p>
            <p
              v-if="selectedSupplier2.address"
              class="address-content"
            >
              {{ formatAddressForDisplay(selectedSupplier2.address) }}
            </p>
            <p
              v-else
              class="text-muted mb-0"
            >
              Address not available
            </p>
          </div>
        </div>
      </b-col>
      <b-col md="6">
        <div class="address-box">
          <h5 class="address-label">
            FROM:
          </h5>
          <p
            v-if="vatikaAddress"
            class="address-content"
          >
            {{ formatAddressForDisplay(vatikaAddress) }}
          </p>
          <p
            v-else
            class="text-muted"
          >
            Loading...
          </p>
        </div>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import {
  BRow, BCol, BFormGroup, BButton, BSpinner, BCard, BCardTitle, BAlert,
} from 'bootstrap-vue'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import axios from 'axios'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BSpinner,
    BCard,
    BCardTitle,
    BAlert,
    CustomVSelect,
  },
  data() {
    return {
      selectedSupplier: null,
      selectedSupplier2: null,
      supplierOptions: [],
      vatikaAddress: null,
      printing: false,
      loadingError: null,
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      try {
        const [suppliersRes, vatikaRes] = await Promise.all([
          axios.get('/supplier_options_with_address'),
          axios.get('/vatika_address'),
        ])

        this.supplierOptions = suppliersRes.data.data || suppliersRes.data
        this.vatikaAddress = vatikaRes.data.data || vatikaRes.data
        this.loadingError = null
      } catch (error) {
        this.loadingError = error.response?.data?.message || 'Failed to load data. Please try again.'
      }
    },
    formatAddressForDisplay(address) {
      if (!address || typeof address !== 'string') return ''
      return address.replace(/\\n/g, '\n')
    },
    formatAddressForPrint(address) {
      if (!address || typeof address !== 'string') return 'Address not available'
      return address.replace(/\\n/g, '\n').replace(/\n/g, '<br>')
    },
    handlePrint() {
      if (!this.selectedSupplier) return

      this.printing = true
      try {
        this.printAddress()
      } finally {
        setTimeout(() => {
          this.printing = false
        }, 500)
      }
    },
    printAddress() {
      const iframe = document.createElement('iframe')
      iframe.style.position = 'absolute'
      iframe.style.top = '-9999px'
      iframe.style.left = '-9999px'
      document.body.appendChild(iframe)

      const formattedVatikaAddress = this.formatAddressForPrint(this.vatikaAddress)
      const formattedSupplierAddress = this.formatAddressForPrint(this.selectedSupplier.address)

      let secondLabelHtml = ''
      if (this.selectedSupplier2) {
        const formattedSupplier2Address = this.formatAddressForPrint(this.selectedSupplier2.address)

        secondLabelHtml = `
      <div class="label-container">
        <div class="header">
          <h2>
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            HANDLE WITH CARE AND CAUTION
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </h2>
        </div>
        
        <div class="address-container">
          <div class="address-section">
            <div class="label">TO,</div>
            <div class="supplier-identifier">${this.selectedSupplier2.identifier || ''}</div>
            <div class="address-text">${formattedSupplier2Address}</div>
          </div>
          <div class="address-section">
            <div class="label">FROM,</div>
            <div class="address-text">${formattedVatikaAddress}</div>
          </div>
        </div>
      </div>
    `
      }

      const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        @page {
          margin: 0.25in;
          size: A4;
        }
        
        * {
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
          print-color-adjust: exact !important;
          box-sizing: border-box;
        }
        
        body {
          font-family: Arial, sans-serif;
          margin: 0;
          padding: 0;
          height: 100vh;
        }
        
        .label-container {
          height: 50vh;
          display: flex;
          flex-direction: column;
          padding: 10px;
          page-break-inside: avoid;
        }
        
        .label-container:first-child {
          border-bottom: 1px dashed #999;
        }
        
        .header {
          text-align: center;
          margin-bottom: 8px;
          padding: 8px;
          border: 2.5px solid #333;
          border-radius: 5px;
          background-color: #fff;
        }
        
        .header h2 {
          margin: 0;
          font-size: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          font-weight: bold;
          color: #000;
          line-height: 1.1;
        }
        
        .icon {
          width: 22px;
          height: 22px;
          flex-shrink: 0;
        }
        
        .address-container {
          display: flex;
          flex-direction: column;
          gap: 8px;
          flex: 1;
        }
        
        .address-section {
          flex: 1;
          padding: 10px 12px;
          border: 2px solid #333;
          border-radius: 5px;
          background-color: #f9f9f9;
          display: flex;
          flex-direction: column;
        }
        
        .label {
          font-weight: bold;
          font-size: 18px;
          margin-bottom: 5px;
          color: #000;
          text-transform: uppercase;
          line-height: 1.1;
          border-bottom: 1px solid #ddd;
          padding-bottom: 3px;
        }
        
        .supplier-identifier {
          font-weight: bold;
          font-size: 22px;
          color: #000;
          margin-bottom: 5px;
          line-height: 1.2;
        }
        
        .address-text {
          font-size: 20px;
          font-weight: bold;
          line-height: 1.25;
          color: #333;
          word-wrap: break-word;
          overflow-wrap: break-word;
          flex: 1;
        }
      </style>
    </head>
    <body>
      <div class="label-container">
        <div class="header">
          <h2>
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            HANDLE WITH CARE AND CAUTION
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </h2>
        </div>
        
        <div class="address-container">
          <div class="address-section">
            <div class="label">TO,</div>
            <div class="supplier-identifier">${this.selectedSupplier.identifier || ''}</div>
            <div class="address-text">${formattedSupplierAddress}</div>
          </div>
          <div class="address-section">
            <div class="label">FROM,</div>
            <div class="address-text">${formattedVatikaAddress}</div>
          </div>
        </div>
      </div>
      ${secondLabelHtml}
    </body>
    </html>
  `

      iframe.contentDocument.open()
      iframe.contentDocument.write(htmlContent)
      iframe.contentDocument.close()

      setTimeout(() => {
        iframe.contentWindow.print()

        iframe.contentWindow.addEventListener('afterprint', () => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe)
          }
        })

        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe)
          }
        }, 500)
      }, 300)
    },
  },
}
</script>

<style scoped>
.address-box {
  padding: 1rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background-color: #f8f9fa;
  min-height: 150px;
}

.address-label {
  font-weight: bold;
  font-size: 1rem;
  margin-bottom: 0.75rem;
  color: #495057;
  text-transform: uppercase;
}

.address-content {
  font-size: 0.95rem;
  line-height: 1.6;
  color: #212529;
  white-space: pre-line;
  margin-bottom: 0;
}
</style>
